import React, { useCallback } from "react";
import { Box, Button, IconButton, Modal, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import CompareCard, { CompareLoadingCard } from "./CompareCard";
import PlayerCompare from "./PlayerCompare";
import ScoutCompare from "./ScoutCompare";
import AgentCompare from "./AgentCompare";
import CoachCompare from "./CoachCompare";
import OtherCompare from "./OtherCompare";

import { colors } from "../../theme/palette";
import { CloseCompareIcon, CompareIcon } from "../../assets/icons/MUI/NetworkIcons";

import { useCompareUsersList } from "../../api/queries/compare/networkCompareQueries";

const CompareModal = ({
  openModal,
  compareData,
  setCompareData,
  closeModal,
}: {
  openModal: boolean;
  compareData: any;
  setCompareData: React.Dispatch<any>;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  const { compareUsersList, isCompareUsersListLoading, isCompareUsersListHasError } = useCompareUsersList(
    compareData,
    openModal
  );
  const ROLE = compareData[0]?.role;

  const clearAll = useCallback(() => {
    setCompareData([]);
    closeModal();
  }, [setCompareData, closeModal]);

  const clearPersona = useCallback(
    (id: number) => {
      if (compareData.length === 2) {
        setCompareData([]);
        closeModal();
      } else {
        setCompareData((prevData: any) => prevData.filter((el: any) => el.id !== id));
      }
    },
    [compareData, setCompareData, closeModal]
  );

  return (
    <Modal open={openModal} onClose={closeModal} aria-labelledby="Player Advanced Search">
      <Box
        sx={{
          background: colors.White,
          position: "absolute",
          top: 0,
          right: 0,
          width: "fit-content",
          maxWidth: "calc(100% - 170px)",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            padding: "32px 27px",
            overflow: "auto",
            flex: 1,
          }}
        >
          {isCompareUsersListHasError ? "Something went wrong" : ""}
          {!isCompareUsersListHasError ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  marginBottom: "52px",
                }}
              >
                <Typography
                  variant="h4ExtraBold"
                  sx={{
                    display: "flex",
                    columnGap: "2px",
                    alignItems: "center",
                  }}
                >
                  <CompareIcon />
                  {t("components.compare.compare")}
                </Typography>
                <IconButton onClick={closeModal}>
                  <CloseCompareIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: "56px",
                  marginBottom: "27px",
                  padding: "0 27px 0 0",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ width: "237px", alignSelf: "end" }}>
                  <Button
                    variant="text-sm"
                    onClick={clearAll}
                    sx={{
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "24px",
                    }}
                  >
                    {t("components.compare.clearAll")}
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: "56px",
                  }}
                >
                  {isCompareUsersListLoading
                    ? [...Array(2)].map(() => <CompareLoadingCard />)
                    : compareUsersList?.items?.map((user: any) => (
                        <Box sx={{ width: "148px" }}>
                          <CompareCard key={user.id} user={user} clearPersona={clearPersona} />
                        </Box>
                      ))}
                </Box>
              </Box>
              {isCompareUsersListLoading && [...Array(3)].map((_, idx) => <CompareLoader isLastRow={idx === 2} />)}
              {!isCompareUsersListLoading && ROLE === "PLAYER" && <PlayerCompare compareUsersList={compareUsersList} />}
              {!isCompareUsersListLoading && ROLE === "SCOUT" && <ScoutCompare compareUsersList={compareUsersList} />}
              {!isCompareUsersListLoading && ROLE === "AGENT" && <AgentCompare compareUsersList={compareUsersList} />}
              {!isCompareUsersListLoading && ROLE === "COACH" && <CoachCompare compareUsersList={compareUsersList} />}
              {!isCompareUsersListLoading && ROLE === "OTHER" && <OtherCompare compareUsersList={compareUsersList} />}
            </>
          ) : (
            <></>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
export default CompareModal;

const CompareLoader = ({ isLastRow }: { isLastRow: boolean }) => {
  return (
    <>
      <Typography
        variant="body16Bold"
        sx={{
          borderRadius: "8px",
          background: colors.Gray50,
          padding: "8px 27px",
          fontWeight: 700,
          display: "block",
        }}
      >
        <Skeleton variant="text" width={125} height={20} />
      </Typography>
      {[...Array(3)].map((_, idx) => (
        <Box
          sx={{
            display: "flex",
            padding: "17px 27px",
            borderBottom: `${!isLastRow && "1px solid #EAECF0"}`,
            gap: "66px",
            "&:last-child": {
              borderBottom: "none",
            },
          }}
        >
          <Box sx={{ width: "237px" }}>
            <Typography
              variant="body14Bold"
              sx={{
                fontWeight: 700,
              }}
            >
              <Skeleton variant="text" width={80} height={17} />
            </Typography>
          </Box>
          {[...Array(2)].map((_, idx) => (
            <Box sx={{ width: "148px", textAlign: "center" }} key={idx}>
              <Typography variant="body14Regular">
                <Skeleton variant="text" width={85} height={24} sx={{ margin: "0 auto" }} />
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </>
  );
};
